import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import useViewport from '../../../hooks/useViewport'
import { selectName, selectScheduleACallUri } from '../../../store/selectors/customerProfile'
import { LeavingHomebotCtaButton } from '../LeavingHomebotCta/LeavingHomebotCtaButton'
import { Text, Flex, Card, Heading, Box, Button, Link, Stack } from '@homebotapp/hb-react-component-catalog'
import { clientEventSourceMapper } from '../../homeSearch/Utils/clientEventSourceMapper'

import { ConfirmPreferredAgentModal } from './ConfirmPreferredAgentModal'
import { useState } from 'react'
import { AddPreferredAgentModal } from './AddPreferredAgent/AddPreferredAgentModal'
import { ConfirmRemovePreferredPartnerModal } from './RemovePreferredAgent/ConfirmRemovePreferredPartnerModal'
import { AvatarBubbles } from '../AvatarBubbles/AvatarBubbles'
import Format from '../Format'
import { ClientTeamMembership } from '../../../types/clientTeamMembership'
import { formatStreetAddress } from '../../../helpers/formatListingAddress'
import { selectAddress } from '../../../store/selectors/home'

interface TeamCardProps {
  teamMemberName: string
  photoUri: string
  corporatePhotoUri?: string
  email: string
  nmls?: string
  license?: string
  phone: string
  title: string
  corporateProfileName: string
  clientTeamMembership?: ClientTeamMembership
  clientId?: string
  isVerified?: boolean
  isPrimary?: boolean
  scheduleACallUri?: string
}

export const MSG = defineMessages({
  scheduleACallHeaderText: {
    id: 'Intro.scheduleACallHeaderText',
    defaultMessage: 'Schedule a call'
  },
  continueText: {
    id: 'Intro.continueText',
    defaultMessage: "Do you want to continue to {name}'s scheduling site in a new tab?"
  },
  verifyTeamMembership: {
    id: 'HomebotNetwork.verifyTeamMembership',
    defaultMessage: 'Do you want them on your Homebot team? Please'
  },
  confirmUnverifiedTeamMembership: {
    id: 'HomebotNetwork.confirmUnverifiedTeamMembership',
    defaultMessage: 'confirm'
  },
  removeUnverifiedTeamMembership: {
    id: 'HomebotNetwork.removeUnverifiedTeamMembership',
    defaultMessage: 'remove'
  },
  orTeamMembership: {
    id: 'HomebotNetwork.orTeamMembership',
    defaultMessage: 'or'
  },
  removeTeamMembership: {
    id: 'HomebotNetwork.removeTeamMembership',
    defaultMessage: 'Remove'
  },
  replaceTeamMembership: {
    id: 'HomebotNetwork.replaceTeamMembership',
    defaultMessage: 'Replace'
  },
  license: {
    id: 'HomebotNetwork.license',
    defaultMessage: 'License #'
  },
  addedByHomebot: {
    id: 'Intro.TeamMemberSection.verifyPreferredAgent.addedByHomebot',
    defaultMessage: 'Homebot added your previous agent from {streetAddress}.'
  },
  addedByCustomer: {
    id: 'Intro.TeamMemberSection.verifyPreferredAgent.addedByCustomer',
    defaultMessage: '{customerFullName} added this {customerType}.'
  }
})

export const TeamCard = ({
  clientId,
  clientTeamMembership,
  teamMemberName,
  phone,
  photoUri,
  corporatePhotoUri,
  email,
  nmls,
  license,
  isVerified,
  isPrimary,
  title,
  scheduleACallUri,
  corporateProfileName
}: TeamCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false)
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false)
  const [isReplaceAction, setIsReplaceAction] = useState(false)
  const intl = useIntl()
  const location = window.location.pathname
  const addedBy = clientTeamMembership?.addedBy
  const streetAddress = useSelector(selectAddress)
  const customerName = useSelector(selectName)

  const scheduleACallRenderModalBody = () => {
    return (
      <Text>
        {intl.formatMessage(MSG.continueText, {
          name: customerName
        })}
      </Text>
    )
  }

  const renderActionButtons = () => {
    if (isPrimary) {
      // Primary users - show license info only, cannot remove or replace
      return (
        license && (
          <Text color='neutral.600' size='3xs' mt={4}>
            {intl.formatMessage(MSG.license)} {license}
          </Text>
        )
      )
    }

    if (!isVerified) {
      // Non-primary, unverified users - show verification options
      return (
        <Box textAlign='left'>
          <Text color='neutral.700' size='xs' mt={4}>
            {addedBy === 'homebot' &&
              `${intl.formatMessage(MSG.addedByHomebot, {
                streetAddress: formatStreetAddress(streetAddress)
              })}${' '}`}
            {addedBy === 'customer' &&
              `${intl.formatMessage(MSG.addedByCustomer, {
                customerFullName: customerName,
                customerType: title.toLowerCase()
              })} `}
            {intl.formatMessage(MSG.verifyTeamMembership)}{' '}
            <Button
              size='xs'
              colorScheme='primary'
              textDecoration='none'
              fontWeight='semibold'
              onClick={() => setIsModalOpen(true)}
              variant='link'
              _hover={{
                color: 'primary.500'
              }}
            >
              {intl.formatMessage(MSG.confirmUnverifiedTeamMembership)}
            </Button>{' '}
            {intl.formatMessage(MSG.orTeamMembership)}{' '}
            <Button
              size='xs'
              colorScheme='primary'
              textDecoration='none'
              fontWeight='semibold'
              onClick={() => {
                setIsConfirmRemoveModalOpen(true)
                setIsReplaceAction(false)
              }}
              variant='link'
              _hover={{
                color: 'primary.500'
              }}
            >
              {intl.formatMessage(MSG.removeUnverifiedTeamMembership)}
            </Button>{' '}
            .
          </Text>
          {license && (
            <Text color='neutral.600' size='3xs'>
              {intl.formatMessage(MSG.license)} {license}
            </Text>
          )}
        </Box>
      )
    }

    // Non-primary, verified users - show remove/replace buttons
    return (
      <Stack direction={['column', 'row']} gap={2} mt={4} width='100%'>
        <Button
          size='sm'
          colorScheme='danger'
          textDecoration='none'
          fontWeight='semibold'
          onClick={() => {
            setIsConfirmRemoveModalOpen(true)
            setIsReplaceAction(false)
          }}
          variant='ghost'
          width={['100%', 'auto']}
          _hover={{
            color: 'primary.500'
          }}
        >
          {intl.formatMessage(MSG.removeTeamMembership)}
        </Button>
        <Button
          size='sm'
          colorScheme='secondary'
          color='neutral.900'
          textDecoration='none'
          fontWeight='semibold'
          onClick={() => {
            setIsReplaceModalOpen(true)
            setIsReplaceAction(true)
          }}
          variant='solid'
          width={['100%', 'auto']}
          _hover={{
            color: 'primary.500'
          }}
        >
          {intl.formatMessage(MSG.replaceTeamMembership)}
        </Button>
      </Stack>
    )
  }

  const handleRemoveSuccess = () => {
    setIsReplaceModalOpen(true)
    setIsReplaceAction(false)
  }
  return (
    <>
      <Card display='flex' data-qa='TeamCard' flexDirection='column' width={['100%', '100%', '375px']}>
        <Flex
          position='relative'
          justifyContent='flex-start'
          gap={2}
          py={0}
          h='70px'
          borderTopRadius='lg'
          borderBottomRadius='none'
          bgColor='neutral.200'
          overflow='visible'
        >
          <Box
            position='relative'
            display='flex'
            alignItems='center'
            p={[6, 0, 7]}
            pl={0}
            bgColor='neutral.200'
            borderTopLeftRadius='inherit'
            borderTopRightRadius={['inherit', null, 0]}
            borderBottomLeftRadius={[0, null, 'inherit']}
            width={['100%', null, '160px']}
            flex='0 0 auto'
          >
            <AvatarBubbles photoUri={photoUri} companyLogo={corporatePhotoUri} />
          </Box>
        </Flex>
        <Flex flexDir='column' p={4} pb={2} alignItems='flex-start' mt={4}>
          <Heading
            mb={1}
            fontWeight='semibold'
            variant='eyebrow'
            color='neutral.700'
            size='2xs'
            textTransform='uppercase'
          >
            {title}
          </Heading>
          <Text mb={1} fontWeight='semibold' color='neutral.900' size='lg'>
            {teamMemberName}
          </Text>
          <Text color='neutral.700' size='xs'>
            {corporateProfileName}
          </Text>
          <Flex gap={2} alignItems='center'>
            <Link
              size='xs'
              href={`tel:${phone}`}
              color='neutral.900'
              textDecoration='none'
              fontWeight='semibold'
              _hover={{
                color: 'primary.500'
              }}
            >
              <Format.Phone value={phone} />
            </Link>
            {scheduleACallUri && (
              <>
                <Text color='neutral.700' mt={3} size='xs'>
                  •
                </Text>
                <LeavingHomebotCtaButton
                  scheduleACall
                  ctaButtonSize='sm'
                  ctaButtonColorScheme='brand'
                  hideIllustration
                  eventSource={clientEventSourceMapper(location)}
                  headerText={intl.formatMessage(MSG.scheduleACallHeaderText)}
                  ctaButtonVariant='link'
                  ctaLinkUrl={scheduleACallUri}
                  renderModalBody={scheduleACallRenderModalBody}
                  tracking={{
                    guid: 'gF3zwC5DoucCspwH8isJDa',
                    ui_context: 'Team Card',
                    descriptor: 'Schedule a call button clicked, Team card'
                  }}
                >
                  {intl.formatMessage(MSG.scheduleACallHeaderText)}
                </LeavingHomebotCtaButton>
              </>
            )}
          </Flex>
          <Link
            size='xs'
            href={`mailto:${email}`}
            color='neutral.900'
            textDecoration='none'
            fontWeight='semibold'
            _hover={{
              color: 'primary.500'
            }}
          >
            {email}
          </Link>

          {renderActionButtons()}

          {nmls && (
            <Text color='neutral.600' size='3xs'>
              NMLS #{nmls}
            </Text>
          )}
        </Flex>
      </Card>
      <ConfirmRemovePreferredPartnerModal
        isOpen={isConfirmRemoveModalOpen}
        onClose={() => setIsConfirmRemoveModalOpen(false)}
        membershipId={clientTeamMembership?.id}
        clientId={clientId}
        onSuccess={handleRemoveSuccess}
        customerType={clientTeamMembership?.customerProfile?.type}
      />
      <AddPreferredAgentModal
        clientId={clientId}
        isOpen={isReplaceModalOpen}
        isReplaceAction={isReplaceAction}
        clientTeamMembership={clientTeamMembership}
        onClose={() => setIsReplaceModalOpen(false)}
        customerType={clientTeamMembership?.customerProfile?.type}
      />
      <ConfirmPreferredAgentModal
        clientId={clientId}
        membershipId={clientTeamMembership?.id}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        customerType={clientTeamMembership?.customerProfile?.type}
      />
    </>
  )
}
